import {Injectable} from '@angular/core'
import {
  Ordering,
  ProDirectoryPage_GetIndustriesGql,
  ProDirectoryPage_GetTop10OfferingsGql,
} from '../../../../__generated__/types'
import {BehaviorSubject, mergeMap} from 'rxjs'
import {map} from 'rxjs/operators'
import {SortingOption} from '../../../craft/sorting/sorting.types'
import {IndustryOverviewPageContentService} from '../../../core/content/services/industry-overview-page-content.service'

export enum Order {
  AlphabeticalAsc = 'ALPHABETICAL_ASC',
  AlphabeticalDesc = 'ALPHABETICAL_DESC',
}

export const defaultSortingOption: SortingOption = {
  name: 'N/A',
  value: Order.AlphabeticalAsc,
  default: true,
}

//
// Some of the logic to load data in this page needs to be refactored, in order to:
//  - align with CraftsmenRegionPagesContentService
//  - Have less network round trips
//
// TODO Make a single query to fetch the page content, side nav industries and top 10 offerings
//  - Use the CMS directly to fetch the side nav links to industry overview pages
//  - Use the CMS to fetch the top 10 offerings
//
// TODO Stop relying on IndustryOverviewPage.proDirectoryIndustries to know which industries to load. This wasn't the purpose of this field and is confusing
//  - Load all industries from the CMS where `hideInGeneratedPages` is not false
//  - The only blocker at the moment is we rely on `industry.offerings` from the backend, to get all offerings for a specific industry
//
@Injectable()
export class ProDirectoryPageService {
  private readonly sort$ = new BehaviorSubject<SortingOption>(defaultSortingOption)

  industries$ = this.sort$.pipe(
    mergeMap(sortingOption =>
      this.contentService.getIndustryOverviewPageSlugs().pipe(map(industrySlugs => ({sortingOption, industrySlugs}))),
    ),
    mergeMap(({industrySlugs, sortingOption}) => {
      const industryIds = Array.from(new Set(industrySlugs.flatMap(i => i.industryIds)))

      const orderBy = {
        alphabetical: sortingOption.value === Order.AlphabeticalAsc ? Ordering.Asc : Ordering.Desc,
      }

      return this.getIndustriesGql.fetch({industryIds, orderBy}).pipe(
        map(result =>
          result.data.industriesByIds.map(industry => {
            const overviewPage = industrySlugs.find(i => i.industryIds.includes(industry.id))
            if (!overviewPage) {
              throw new RangeError(`Industry ${industry.label} doesn't have a page in CMS`)
            }
            return {
              ...industry,
              overviewPage,
            }
          }),
        ),
      )
    }),
  )

  top10Offerings$ = this.getTop10OfferingsGql.fetch().pipe(
    map(result => result.data.searchOfferings),
    map(result => [...result].sort((a, b) => a.label.localeCompare(b.label))),
  )

  constructor(
    private readonly contentService: IndustryOverviewPageContentService,
    private readonly getIndustriesGql: ProDirectoryPage_GetIndustriesGql,
    private readonly getTop10OfferingsGql: ProDirectoryPage_GetTop10OfferingsGql,
  ) {}

  refresh(sortingOption: SortingOption) {
    this.sort$.next(sortingOption)
  }
}
